import { useActivityImage } from '../../api/queries/live/activityImage';
import { LiveDataDetailImageQueryGuarded } from '../DetailView/DetailImageQuery';
import { useActivitySoilClassification } from '../../api/queries/live/activitySoilClassification';
import { useActivityEdgeEstimation } from '../../api/queries/live/activityEdgeEstimation';
import { PredictionColumnsLayout } from '../Prediction/PredictionColumnsLayout';
import { LiveDataAttributesColumnQueryGuarded } from '../Prediction/AttributesColumn/AttributesColumn';
import { LiveDataMaterialColumnQueryGuarded } from '../Prediction/MaterialColumn/MaterialColumn';
import { useActivityMaterialClassification } from '../../api/queries/live/activityMaterialClassification';
import { useActivityVolumeEstimation } from '../../api/queries/live/activityVolumeEstimation';
import { shouldDisplayEdgeEstimation, shouldDisplaySoilClassification } from '../Prediction/utils';
import { useActivityLicensePlate } from '../../api/queries/live/activityLicensePlate';
import { memo, useMemo } from 'react';
import { useResolvedBillableMaterials } from '../BillableMaterials/useResolvedBillableMaterials';
import BillableMaterialColumn from '../BillableMaterials/predictionColumn/BillableMaterialColumn';
import { ActivityTileLayout } from './ActivityTileLayout';
import { useEdgeSizeBoundingBoxes } from '../../utils/useEdgeSizeBoundingBoxes';
import { ResourceStatus } from '../../api/resources/useOcInsightsEvents';

export interface ActivityRecording {
  id: string;
  captureStartAt: string;
  imageStatus: ResourceStatus;
  materialStatus: ResourceStatus;
  soilStatus: ResourceStatus;
  volumeStatus: ResourceStatus;
  edgeStatus: ResourceStatus;
  licensePlateStatus: ResourceStatus;
  licensePlate: string;
}

interface ActivityTileProps {
  organisationId: string;
  jobsiteId: string;
  recording: ActivityRecording;
  isFocus: boolean;
}

export default memo(function ActivityTile({ organisationId, jobsiteId, recording, isFocus }: ActivityTileProps) {
  const captureStartAt = recording.captureStartAt;

  const image = useActivityImage({
    jobsiteId,
    captureStartAt,
    enabled: recording.imageStatus === 'finished',
  });

  const licensePlateQuery = useActivityLicensePlate({
    jobsiteId,
    captureStartAt,
    enabled: recording.licensePlateStatus === 'finished' && !recording.licensePlate,
  });

  const licensePlate = recording.licensePlate || licensePlateQuery.data || '—';

  const materialClassification = useActivityMaterialClassification({
    jobsiteId,
    captureStartAt,
    enabled: recording.materialStatus === 'finished',
  });

  const displaySoilClassification = useMemo(
    () => shouldDisplaySoilClassification(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const soilClassification = useActivitySoilClassification({
    jobsiteId,
    captureStartAt,
    enabled: recording.soilStatus === 'finished' && displaySoilClassification,
  });

  const displayEdgeEstimation = useMemo(
    () => shouldDisplayEdgeEstimation(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const edgeEstimation = useActivityEdgeEstimation({
    jobsiteId,
    captureStartAt,
    enabled: recording.edgeStatus === 'finished' && displayEdgeEstimation,
  });

  const volumeEstimation = useActivityVolumeEstimation({
    jobsiteId,
    captureStartAt,
    enabled: recording.volumeStatus === 'finished',
  });

  const { resolvedMaterials } = useResolvedBillableMaterials(
    organisationId,
    jobsiteId,
    materialClassification.data,
    soilClassification.data,
    edgeEstimation.data
  );

  const { selectedAttribute, edgeSizesBoundingBoxes, handleAttributeSelect } = useEdgeSizeBoundingBoxes(
    edgeEstimation.data
  );

  return (
    <ActivityTileLayout captureStartAt={captureStartAt} licensePlate={licensePlate} isFocus={isFocus}>
      <LiveDataDetailImageQueryGuarded
        title={'delivery.loading_area'}
        imageQuery={image}
        downloadFileName={`${captureStartAt}.jpg`}
        imageStatus={recording.imageStatus}
        boundingBoxes={edgeSizesBoundingBoxes}
      />
      <PredictionColumnsLayout
        materialTypeColumn={
          <LiveDataMaterialColumnQueryGuarded
            materialClassificationQuery={materialClassification}
            resourceStatus={recording.materialStatus}
          />
        }
        attributesColumn={
          <LiveDataAttributesColumnQueryGuarded
            attributes={[
              { resourceStatus: recording.edgeStatus, query: edgeEstimation },
              { resourceStatus: recording.soilStatus, query: soilClassification },
              { resourceStatus: recording.volumeStatus, query: volumeEstimation },
            ]}
            isMaterialClassificationPending={materialClassification.isPending}
            selectedAttribute={selectedAttribute}
            onAttributeSelect={handleAttributeSelect}
          />
        }
        billableMaterialColumn={<BillableMaterialColumn isUserAdmin={false} materials={resolvedMaterials} />}
      />
    </ActivityTileLayout>
  );
});
